import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import moment from "moment";
import * as yup from "yup";

import pathnames from "routes/pathnames";

import api from "services/api";
import getClientPlatform from "services/get-client-platform";

import formatDateTime from "common/format-date-time";
import serveRequestErrors from "common/serve-request-errors";
import serveRequestSuccess from "common/serve-request-success";

import ERRORS from "constants/errors";
import CONSTANTS from "constants/constant";
import { statusOptions } from "constants/status-options";

import ActionEnum from "enum/action";

import ErrorResponseDto from "dto/services/error-response-dto";
import PageClientDto from "dto/pages/clients/page-client-dto";

import AppInput from "components/app-input";
import AppButton from "components/app-button";
import AppHeading from "components/app-heading";
import AppTextarea from "components/app-textarea";
import AppBreadcrumbs from "components/app-breadcrumbs";
import AppSelectInput from "components/app-select-input";

const PageClient = () => {
	const navigate = useNavigate();
	const { id } = useParams();
	const { date, time } = formatDateTime(String(new Date()));
	const isAddNew = useMemo(() => (id === ActionEnum.Create ? true : false), [id]);
	const buttonLabel = useMemo(() => (isAddNew ? "Add" : "Save"), [isAddNew]);

	//prettier-ignore
	const breadcrumbItems = useMemo(() => [
        { label: "Clients Project", href: pathnames.clients },
        { label: isAddNew ? "Add New" : "Edit Client Project", href: isAddNew ? pathnames.client + "/create" : `${pathnames.clients}/${id}` },
    ], [id, isAddNew]);

	const validationSchema = yup.object({
		title: yup.string().max(90).required(ERRORS.REQUIRED),
		status: yup.string().required(ERRORS.REQUIRED),
		cover: yup.object().shape({
			mediaType: yup.string().required(ERRORS.REQUIRED),
		}),
		descriptionShort: yup.string().required(ERRORS.REQUIRED),
	});

	const formik = useFormik<PageClientDto>({
		initialValues: {
			title: "",
			cover: { mediaType: "", backgroundURL: "", imageURL: "", thumbnailURL: "" },
			projectType: "",
			status: "",
			publicationDate: date,
			updatedAt: `${date} | ${time}`,
			description: "",
			descriptionShort: "",
		},
		validationSchema,
		onSubmit: (values) => {
			onHandleSubmit(values);
		},
	});

	//prettier-ignore
	const onHandleSubmit = useCallback(async (values: PageClientDto) => {
        let response = null;
 
        try {
            const payload = {
                title: values?.title || "",
                cover: values?.cover || {},
                publicationDate: moment(values?.publicationDate, CONSTANTS.LISTING_DATE_FORMAT).toISOString() || "",
                status: values?.status || "",
				description: values.description || "",
				descriptionShort: values.descriptionShort || "",
				content: [],
				projectType: values.projectType || ""
            };
 
            response = !isAddNew && id ? await api.put.client.update({id: id}, payload) : await api.post.client.create(payload);
        } catch (error) {
            const err = error as Error | ErrorResponseDto;
 
            serveRequestErrors(err);
        }
 
        if (response) {
            navigate(pathnames.clients);

            if (isAddNew) serveRequestSuccess(CONSTANTS.MESSAGES.CREATED, ActionEnum.Create);
            else serveRequestSuccess(CONSTANTS.MESSAGES.UPDATED, ActionEnum.Update);
        }
     }, [navigate, id, isAddNew]);

	const onHandleDelete = useCallback(async () => {
		let response = null;

		try {
			const payload = {
				title: formik.values?.title || "",
				cover: formik.values?.cover || {},
				projectType: formik.values?.projectType || "",
				publicationDate: moment(formik.values?.publicationDate, CONSTANTS.LISTING_DATE_FORMAT).toISOString() || "",
				description: formik.values.description || "",
				descriptionShort: formik.values.descriptionShort || "",
				status: "deleted",
				content: [],
			};

			if (id) response = await api.put.client.update({ id: id }, payload);
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}

		if (response) {
			navigate(pathnames.clients);

			serveRequestSuccess(CONSTANTS.MESSAGES.UPDATED, ActionEnum.Update);
		}
	}, [navigate, id, formik.values]);

	const onHandleGetClientDetails = useCallback(async () => {
		try {
			const response = await api.get.client.client({ id: id! });

			const clientDetails = response.data.data;

			formik.setValues({
				title: clientDetails.title,
				cover: {
					mediaType: clientDetails.cover.mediaType,
					backgroundURL: clientDetails.cover.backgroundURL,
					imageURL: clientDetails.cover.imageURL,
					thumbnailURL: clientDetails.cover.thumbnailURL,
				},
				projectType: clientDetails.projectType || "",
				status: clientDetails.status || "",
				publicationDate: clientDetails.publicationDate ? moment(clientDetails.publicationDate).format(CONSTANTS.LISTING_DATE_FORMAT) : "",
				updatedAt: clientDetails.updatedAt ? moment(clientDetails.updatedAt).format(CONSTANTS.LISTING_DATE_FORMAT) : "",
				description: clientDetails.description || "",
				descriptionShort: clientDetails.descriptionShort || "",
			});
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, formik.setValues]);

	useEffect(() => {
		if (!isAddNew) onHandleGetClientDetails();
	}, [isAddNew, onHandleGetClientDetails]);

	return (
		<div className="page-client">
			<div className="client">
				<AppHeading title="Clients Project">
					<AppHeading.Divider />
				</AppHeading>

				<form className="client__container" onSubmit={formik.handleSubmit}>
					<div className="client__header">
						<AppBreadcrumbs items={breadcrumbItems} />

						<div className="client__button-wrapper">
							{!isAddNew && <AppButton type="button" label="Delete" disabled={formik.isSubmitting} text onClick={onHandleDelete} />}

							<AppButton type="submit" label={buttonLabel} disabled={formik.isSubmitting} />
						</div>
					</div>

					<div className="form">
						<p className="form__title">Client Project Setting</p>

						<div className="form__wrapper">
							{/* prettier-ignore */}
							<div className="full-width">
								<AppInput type="text" name="title" label="Client Project Headline" placeholder="Enter project headline" value={formik.values.title} touched={formik.touched.title} error={formik.errors.title} onChange={formik.handleChange} maxLength={50} required />
							</div>

							{/* prettier-ignore */}
							<AppInput type="text" name="cover.thumbnailURL" label="Thumbnail Image URL" placeholder="Enter URL" value={formik.values.cover.thumbnailURL} touched={formik.touched.cover?.thumbnailURL} error={formik.errors.cover?.thumbnailURL} onChange={formik.handleChange} />

							{/* prettier-ignore */}
							<AppInput type="text" name="cover.imageURL" label="Image URL" placeholder="Enter URL" value={formik.values.cover.imageURL} touched={formik.touched.cover?.imageURL} error={formik.errors.cover?.imageURL} onChange={formik.handleChange} tooltipContent="Please upload a square-sized cover image (e.g., 804x729px)" />

							{/* prettier-ignore */}
							<AppSelectInput type="text" name="cover.mediaType" label="Type" placeholder={"Please select"} value={formik.values.cover.mediaType} touched={formik.touched.cover?.mediaType} error={formik.errors.cover?.mediaType} loadOptions={getClientPlatform} onChange={formik.handleChange} required />

							{/* prettier-ignore */}
							<AppInput type="text" name="cover.backgroundURL" label="Background Image URL" placeholder="Enter URL" value={formik.values.cover.backgroundURL} touched={formik.touched.cover?.backgroundURL} error={formik.errors.cover?.backgroundURL} onChange={formik.handleChange} />

							{/* prettier-ignore */}
							<AppInput type="text" name="updatedAt" label="Last Update" value={formik.values.updatedAt} touched={formik.touched.updatedAt} error={formik.errors.updatedAt} onChange={formik.handleChange} disabled />

							{/* prettier-ignore */}
							<AppInput type="text" name="publicationDate" label="Publication Date" value={formik.values.publicationDate} touched={formik.touched.publicationDate} error={formik.errors.publicationDate} onChange={formik.handleChange} disabled />

							{/* prettier-ignore */}
							<AppInput type="text" name="projectType" label="Project Type" value={formik.values.projectType} touched={formik.touched.projectType} error={formik.errors.projectType} onChange={formik.handleChange} />

							{/* prettier-ignore */}
							<AppSelectInput type="text" name="status" label="Status" placeholder="Please select" value={formik.values.status} touched={formik.touched.status} error={formik.errors.status} options={statusOptions} onChange={formik.handleChange} required />

							{/* prettier-ignore */}
							<div className="full-width">
								<AppTextarea name="descriptionShort" label="Short Description" placeholder="Enter description" value={formik.values.descriptionShort} touched={formik.touched.descriptionShort} error={formik.errors.descriptionShort} onChange={(e) => formik.setFieldValue("descriptionShort", e.target.value)} rows={3} maxRows={5} maxCharLength={150} required />
							</div>

							{/* prettier-ignore */}
							<div className="full-width">
								<AppTextarea name="description" label="Description" placeholder="Enter description" value={formik.values.description} touched={formik.touched.description} error={formik.errors.description} onChange={(e) => formik.setFieldValue("description", e.target.value)} rows={5} maxRows={5} maxCharLength={300} />
							</div>
						</div>
					</div>
				</form>
			</div>
		</div>
	);
};

export default PageClient;
